import { ApiError } from '@core/lib/api/client';
import { cn } from '@core/lib/cn';

import { Button } from './Button';

export interface ErrorStateProps {
  /** The failure. An ApiError gets its server-written message shown verbatim. */
  error: unknown;
  /** Wire this to the query's `refetch`. Omit only when retrying is meaningless. */
  onRetry?: () => void;
  /** True while the retry is in flight, so the button can show its own progress. */
  retrying?: boolean;
  /** Overrides the heading. Defaults to something appropriate for the error kind. */
  title?: string;
  className?: string;
}

/**
 * The failure screen. Always offers a way forward.
 *
 * Three things this does that a naive error view does not:
 *
 *   1. Shows the SERVER'S message. The backend writes customer-facing prose for
 *      every AppError; replacing it with "Something went wrong" throws away the
 *      one sentence that tells the user what to actually do.
 *   2. Distinguishes offline from unreachable from broken. "You're offline" tells
 *      the user to check their connection; "our server had a problem" tells them
 *      to wait. Collapsing both into one message guarantees half of readers do
 *      the wrong thing.
 *   3. Surfaces the request id. It is the only handle support has to find the
 *      matching server log line, and asking a customer to reproduce a bug is much
 *      worse than asking them to read out eight characters.
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  title,
  className,
}: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;

  const heading =
    title ??
    (apiError?.isOffline
      ? "You're offline"
      : apiError?.kind === 'network'
        ? "Can't reach the server"
        : apiError?.kind === 'timeout'
          ? 'That took too long'
          : 'Something went wrong');

  const message =
    apiError?.message ??
    (error instanceof Error ? error.message : 'An unexpected problem stopped this from loading.');

  // A 4xx will fail again identically, so do not invite the user to retry it.
  const retryable = !apiError || apiError.isRetryable || apiError.isOffline;

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-card border border-danger-border bg-danger-bg px-6 py-10 text-center',
        className,
      )}
      role="alert"
    >
      <span aria-hidden="true" className="text-3xl">
        {apiError?.isOffline ? '📡' : '⚠️'}
      </span>
      <h2 className="text-lg font-semibold text-ink">{heading}</h2>
      <p className="max-w-sm text-sm text-ink-muted">{message}</p>

      {onRetry && retryable ? (
        <Button
          variant="secondary"
          onClick={onRetry}
          loading={retrying}
          loadingText="Retrying…"
          className="mt-2"
        >
          Try again
        </Button>
      ) : null}

      {apiError?.requestId ? (
        <p className="mt-2 text-xs text-ink-subtle">
          Reference: <code className="select-all font-mono">{apiError.requestId.slice(0, 12)}</code>
        </p>
      ) : null}
    </div>
  );
}
